import { useEffect } from "react";
import useParks from "../../hooks/api-calls/useParks";
import useSpecies from "../../hooks/api-calls/useSpecies";
import useUsers from "../../hooks/api-calls/useUsers";
import useWindTurbine from "../../hooks/api-calls/useWindTurbine";
import CustomSelect from "../forms/CustomSelect";
import { RootState } from "../../redux/store";
import { useSelector } from "react-redux";
import { AuthState } from "../../redux/authSlice";

interface EntitySelectorsProps {
    formik: any;
    setIsProtected? : (isProtected: boolean) => void;
    isFromPark?: boolean;
    speciesAvailabilityFilter?: { carrionAvailable?: boolean; issueAvailable?: boolean };
}
const EntitySelectors: React.FC<EntitySelectorsProps> = ({ formik , setIsProtected, isFromPark, speciesAvailabilityFilter }) => {

    // HOOKS

    const { getParksList } = useParks();
    const { getWindTurbinesList , updateWindTurbineHookFilters } = useWindTurbine();
    const { getUsersList } = useUsers();
    const { getSpeciesListScientific } = useSpecies(speciesAvailabilityFilter ?? {});
    const { user }: AuthState = useSelector((state: RootState) => state.auth);

    const isAdmin = user?.roles?.includes('Coordinador') || user?.roles?.includes('Administrador') || user?.roles?.includes('Superadministrador') ? true : false;

    // USE EFFECTS

    useEffect(() => {
        updateWindTurbineHookFilters({
            filter_filters: {
                park: formik.values.parkId,
            },
        });
    }, [formik.values.parkId]);

    useEffect(() => {
        if (!isAdmin && user?.id) {
            formik.setFieldValue('userId', user.id);
        }
    }, [isAdmin, user?.id]);

    // RENDER

    return (
        <div className="grid md:grid-cols-2 grid-cols-1 gap-2">

        {/* PARK SELECTOR */}

        <CustomSelect
                isSearchable
                id={'park'}
                label="Parque Eólico"
                isDisabled={isFromPark}
                options={getParksList()}
                onChange={(e: any) => { 
                    formik.setFieldValue('parkId', e.value)
                }}
                value={
                    getParksList().filter((park: any) => park.value === formik.values.parkId)
                }
                required
                error={formik.errors.parkId}
            />

        {/* TURBINE SELECTOR */}

        <CustomSelect
                isSearchable
                id={'turbineId'}
                label="Aerogenerador"
                options={getWindTurbinesList()}
                onChange={(e: any) => {
                    formik.setFieldValue('windTurbineId', e.value)
                }}
                value={
                    getWindTurbinesList().filter((turbine: any) => turbine.value === formik.values.windTurbineId)
                }
                error={formik.errors.windTurbineId}
            />

        {/* USER SELECTOR */}    

        <CustomSelect
                isSearchable
                id={'userId'}
                label="Observador"
                options={getUsersList()}
                onChange={(e: any) => {
                    formik.setFieldValue('userId', e.value)
                }}
                value={
                    isAdmin ? getUsersList().filter((user: any) => user.value === formik.values.userId) : user ? [{
                        value: user.id,
                        label: user.name + ' ' + (user.lastName ? `${user.lastName}` : '')
                    }] : []
                }
                required
                error={formik.errors.userId}
                isDisabled={!isAdmin && user?.id ? true : false}
            />

        {/* SPECIE SELECTOR */}

        <CustomSelect
                isSearchable
                id={'specieId'}
                label="Especie"
                italic
                options={getSpeciesListScientific()}
                onChange={(e: any) => {
                    formik.setFieldValue('specieId', e.value)
                    if (setIsProtected) {
                        if (e.catAndalucia || e.catNacional) {
                            setIsProtected(true);
                            formik.setFieldValue('isProtected', true);
                            // Establecer campos requeridos cuando la especie está protegida
                            formik.setFieldValue('agentSignature', formik.values.agentSignature || '');
                            formik.setFieldValue('agentIdentifier', formik.values.agentIdentifier || '');
                        }
                        else {
                            setIsProtected(false);
                            formik.setFieldValue('isProtected', false);
                            // Limpiar campos cuando la especie no está protegida
                            formik.setFieldValue('agentSignature', '');
                            formik.setFieldValue('agentIdentifier', '');
                        }
                    }
                }}
                value={
                    getSpeciesListScientific().filter((specie: any) => specie.value === formik.values.specieId)
                }
                required
                error={formik.errors.specieId}
            />

        </div>

    )
}

export default EntitySelectors;